export type TextareaSegment =
  | { key: string; text: string; type: 'text' }
  | { code: string; key: string; language: string; type: 'fence' };

const FENCE_OPEN = /^ {0,3}```([A-Za-z][\w-]*)[ \t]*$/;
const FENCE_CLOSE = /^ {0,3}```[ \t]*$/;

type OpenFence = { at: number; language: string; lines: string[] };

export const TextareaFenceUtil = {
  /** `isDrawn` is asked by language as a fence opens, then about its body once it closes. */
  split({
    isDrawn,
    text,
  }: {
    isDrawn: (fence: { code: string; language: string }) => boolean;
    text: string;
  }): TextareaSegment[] {
    const segments: TextareaSegment[] = [];
    let prose: string[] = [];
    let proseLine = 0;
    let fence: OpenFence | null = null;
    let passingThrough = false;

    const openProse = (lineIdx: number) => {
      if (prose.length === 0) proseLine = lineIdx;
    };

    const flushProse = () => {
      const proseText = prose.join('\n');

      if (proseText.trim()) {
        segments.push({
          key: `text-${proseLine}`,
          text: proseText.replaceAll(/^\n+|\n+$/g, ''),
          type: 'text',
        });
      }

      prose = [];
    };

    const keepAsProse = (
      { at, language, lines }: OpenFence,
      closed: boolean
    ) => {
      openProse(at);
      prose.push(`\`\`\`${language}`, ...lines);

      if (closed) prose.push('```');
    };

    for (const [lineIdx, line] of text.split(/\r?\n/).entries()) {
      const open = FENCE_OPEN.exec(line);
      const openLanguage = open?.[1]?.toLowerCase() ?? '';

      if (fence !== null) {
        if (FENCE_CLOSE.test(line)) {
          const code = fence.lines.join('\n').trim();

          if (code && isDrawn({ code, language: fence.language })) {
            flushProse();
            segments.push({
              code,
              key: `${fence.language}-${fence.at}`,
              language: fence.language,
              type: 'fence',
            });
          } else if (code) {
            keepAsProse(fence, true);
          }

          fence = null;
        } else {
          fence.lines.push(line);
        }
      } else if (passingThrough) {
        // A fence we do not draw runs to its close, so an example inside it is never read as one.
        if (FENCE_CLOSE.test(line)) passingThrough = false;

        openProse(lineIdx);
        prose.push(line);
      } else if (open && isDrawn({ code: '', language: openLanguage })) {
        fence = { at: lineIdx, language: openLanguage, lines: [] };
      } else {
        if (open) passingThrough = true;

        openProse(lineIdx);
        prose.push(line);
      }
    }

    if (fence !== null) keepAsProse(fence, false);

    flushProse();

    return segments;
  },
};
